'use client';
import { Button } from '@bfe/moly';
import { Component, ErrorInfo, ReactNode } from 'react';
import './index.scss';

enum EnumErrorType {
  LazyLoadingFailure = 'Lazy Loading Failure',
  RuntimeError = 'Runtime Error',
}

// 定义ErrorBoundary的Props
interface Props {
  children: ReactNode;
  isBlock?: boolean;
  message?: string;
  btnText?: string;
  theme?: 'dark' | 'light';
  renderError?: (
    retry: () => void,
    theme: string,
    message?: string,
    btnText?: string,
  ) => JSX.Element; // 自定义错误渲染函数
}

// 定义ErrorBoundary的State
interface State {
  hasError: boolean;
  errorType: string;
  retryCount: number;
  errorInfo: ErrorInfo | null; // Add the errorInfo property
}

export interface ReportErrorInfo {
  name: string;
  msg: string;
  type: string;
  path: string;
}

class InnerErrorBoundary extends Component<Props, State> {
  state = {
    errorInfo: null,
    hasError: false,
    errorType: '',
    retryCount: 0, // 添加重试计数器
  };

  static getDerivedStateFromError(): State {
    return { hasError: true, errorType: '', retryCount: 0, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const errorType = error.message.includes(
      'Failed to fetch dynamically imported module',
    )
      ? EnumErrorType.LazyLoadingFailure
      : EnumErrorType.RuntimeError;
    this.reportError({
      name: 'MolyErrorBoundary',
      msg:
        errorType === EnumErrorType.LazyLoadingFailure
          ? error.message
          : (error.stack as string),
      type: errorType,
      path: window.location.href,
    });

    this.setState({ errorType, errorInfo: errorInfo });
  }

  reportError(data: ReportErrorInfo): void {
    //@ts-ignore
    if (typeof window !== 'undefined' && window.Monitor && typeof window.Monitor.log === 'function') {
      // 尝试发送上报
      //@ts-ignore
      window.Monitor.log(data);
    } else {
      // 若Monitor不存在或log方法不可用，记录到控制台
      console.warn('[MolyErrorBoundary] Monitor.log is not available.');
    }
  }

  retry = () => {
    if (this.state.errorType === EnumErrorType.LazyLoadingFailure) {
      return location.reload();
    } else {
      this.setState((prevState) => ({
        hasError: false,
        retryCount: prevState.retryCount + 1, // 更新重试计数
      }));
    }
  };

  getCurrentTheme() {
    if (this.props.theme) {
      return this.props.theme;
    }
    return 'light';
  }

  renderBlock() {
    const theme = this.getCurrentTheme();
    const imgSrc =
      theme === 'dark'
        ? '//s1.bycsi.com/bybit/deadpool/image-aaa1bc20cd2242c99497a5bd32355476.png'
        : '//s1.bycsi.com/bybit/deadpool/image-f35884e0f6ae45d7b23451ff8e7dbedf.png';
    return (
      <div className={`moly-errorBoundaryBlock moly-error-${theme}`}>
        <img
          className={`moly-errorBoundaryBlock__img`}
          src={imgSrc}
          width={200}
          height={200}
        />
        <div className={`moly-errorBoundaryBlock__title`}>
          {this.props.message}
        </div>
        <Button
          variant="secondary"
          className="moly-errorBounddary-button"
          size="x-small"
          onClick={this.retry}
        >
          {this.props.btnText}
        </Button>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      if (this.props.renderError) {
        // 使用传入的自定义渲染函数
        return this.props.renderError(
          this.retry,
          this.getCurrentTheme(),
          this.props.message,
          this.props.btnText,
        );
      } else {
        return this.renderBlock();
      }
    }
    return this.props.children;
  }
}

export default function ErrorBoundary(props: Props) {
  const innerProps = { ...props, theme: props.theme || 'light' };
  return <InnerErrorBoundary {...innerProps} />;
}
